import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../auth/client';
import { useAppDispatch, useAppSelector } from './store';
import {
  removeActiveJob,
  triggerStatusRefresh,
} from '../modules/ContractDraft/store/contractDraftDashboard.slice';
import {
  CONTRACT_DRAFT_TOAST_MESSAGES,
  TOAST_CONFIG,
} from '../modules/ContractDraft/constants/toastMessages';
import { API_ENDPOINTS } from '../modules/ContractDraft/Pages/ContractDraftingDetail/constants';
import { claimDraftNotification } from '../modules/ContractDraft/utils/draftNotifyRegistry';

const GLOBAL_POLL_INTERVAL_MS = 15000;

export const useGlobalJobPoller = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const activeJobs = useAppSelector(
    (state) => state.rootReducer?.contractDraftDashboardReducer?.activeJobs || {}
  );

  const activeJobsRef = useRef(activeJobs);
  activeJobsRef.current = activeJobs;

  useEffect(() => {
    const jobKeys = Object.keys(activeJobs);
    if (jobKeys.length === 0) return;

    const checkJobStatuses = async () => {
      const currentJobs = activeJobsRef.current;
      const keys = Object.keys(currentJobs);
      if (keys.length === 0) return;

      for (const requestId of keys) {
        const job = currentJobs[requestId];
        if (!job) continue;

        try {
          const res = await client.get(API_ENDPOINTS.CONTRACT_REQUEST(requestId));
          const status = res.data?.status?.toUpperCase();

          // The detail page's transition effect can also detect this same
          // completion. `claimDraftNotification` guarantees exactly one of the
          // two observers shows the toast (whoever claims first), without ever
          // dropping the notification. The job is always cleaned up regardless
          // of which observer showed the toast.
          if (status === 'DRAFT_GENERATED') {
            if (claimDraftNotification(requestId)) {
              const toastMessage = CONTRACT_DRAFT_TOAST_MESSAGES.DRAFTING.GENERATION_SUCCESS(
                job.projectName ?? undefined
              );

              toast.success(toastMessage, {
                ...TOAST_CONFIG.SUCCESS,
                toastId: `global-draft-ready-${requestId}`,
                autoClose: 8000,
                style: { cursor: 'pointer' },
                onClick: () => navigate(`/contract-draft/${requestId}`),
              });
            }

            dispatch(removeActiveJob(requestId));
            dispatch(triggerStatusRefresh());
          } else if (status === 'NEEDS_ATTENTION' || status === 'ACTION_REQUIRED') {
            const failedStep = res.data?.failed_step?.toUpperCase();
            if (
              (failedStep === 'DRAFTING' || !failedStep) &&
              claimDraftNotification(requestId)
            ) {
              toast.error(
                CONTRACT_DRAFT_TOAST_MESSAGES.DRAFTING.GENERATION_ERROR,
                {
                  ...TOAST_CONFIG.ERROR,
                  toastId: `global-draft-failed-${requestId}`,
                  autoClose: 8000,
                  style: { cursor: 'pointer' },
                  onClick: () => navigate(`/contract-draft/${requestId}`),
                }
              );
            }

            dispatch(removeActiveJob(requestId));
            dispatch(triggerStatusRefresh());
          }
        } catch (error) {
          console.error(`[useGlobalJobPoller] Error checking status for ${requestId}:`, error);
        }
      }
    };

    const intervalId = setInterval(checkJobStatuses, GLOBAL_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [activeJobs, dispatch, navigate]);
};

export default useGlobalJobPoller;
